import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { MbmProjectFormValues } from '../schemas/mbm-project-form-schema';
import {
  createMbmProject,
  getMbmCustomers,
  getMbmProjects,
  updateMbmProject,
} from '../services/mbm-project.service';
import { GetMbmProjectsParams, MbmProject } from '../types/mbm-project.types';

const MBM_PROJECTS_KEY = 'hr-mbm-projects';

// The timesheet screens keep their own 30-minute copy of the MBM catalog for their pickers. A
// project changed here has to reach them straight away, or it stays missing (or stale) there.
const TIMESHEET_LOOKUP_KEYS = ['hr-mbm-projects-lookup', 'hr-timesheet-manager-mbm-projects'];

const invalidateMbmProjectQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all(
    [MBM_PROJECTS_KEY, ...TIMESHEET_LOOKUP_KEYS].map((key) =>
      queryClient.invalidateQueries({ queryKey: [key] })
    )
  );

export const useGetMbmProjects = (params: GetMbmProjectsParams) => {
  return useGlobalQuery({
    queryKey: [MBM_PROJECTS_KEY, params],
    queryFn: () => getMbmProjects(params),
    staleTime: 60 * 1000,
  });
};

export const useGetMbmCustomers = () => {
  return useGlobalQuery({
    queryKey: [MBM_PROJECTS_KEY, 'customers'],
    queryFn: () => getMbmCustomers(),
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreateMbmProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (values: MbmProjectFormValues) => createMbmProject(values),
    onSuccess: () => {
      invalidateMbmProjectQueries(queryClient);
      toast({ title: t('MBM_PROJECT_CREATED') });
    },
  });
};

export const useUpdateMbmProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { project: MbmProject; values: MbmProjectFormValues }) =>
      updateMbmProject(params),
    onSuccess: (changed) => {
      if (!changed) return;
      invalidateMbmProjectQueries(queryClient);
      toast({ title: t('MBM_PROJECT_UPDATED') });
    },
  });
};
